import { Component, h, Prop, State, Watch } from '@stencil/core';
import { debounce } from 'debounce';
import { CmsImage } from '../../types/global';

interface UiCmsQuotationBodyBase {
  quotation: string;
  authorInfo?: string;
  author: string;
}

interface UiCmsQuotationBodySimple extends UiCmsQuotationBodyBase {
  style: 'simple';
  authorImage?: CmsImage;
}

interface UiCmsQuotationBodyOnImage extends UiCmsQuotationBodyBase {
  style: 'on-image';
  backgroundImage: CmsImage;
  colorScheme?: 'dark-bg' | 'light-bg';
  position?: 'left-top' | 'left-bottom' | 'right-top' | 'right-bottom';
  highContrast?: boolean;
}

type UiCmsQuotationBody = UiCmsQuotationBodySimple | UiCmsQuotationBodyOnImage;

@Component({
  tag: 'ui-cms-quotation',
  styleUrl: 'ui-cms-quotation.scss',
  shadow: true,
})
export class UiCmsQuotation {
  @Prop({ mutable: true }) body: UiCmsQuotationBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop() inverted?: boolean;

  @Watch('utilityBody')
  bodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @State() textOverflows: boolean = false;

  private resizeObserver: ResizeObserver;

  private textRef: HTMLElement;

  private wrapperRef: HTMLElement;

  private backgroundImageRef: HTMLElement;

  private quotationRef: HTMLElement;

  private metaRef: HTMLElement;

  private quotationWidthAtLastDetectedOverflow: number;

  componentWillLoad() {
    this.bodyChangeHandler(this.utilityBody);
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  componentDidLoad() {
    this.addResizeObserver();
    this.checkTextOverflow();
  }

  private addResizeObserver() {
    this.resizeObserver = new ResizeObserver(debounce(() => this.checkTextOverflow(), 100));
    this.resizeObserver.observe(this.wrapperRef);
  }

  private checkTextOverflow() {
    if (this.body.style !== 'on-image') return;

    // If the text is bigger than the available space minus the meta,
    // it should be places below the image instead of on it.
    // Clipping the text is not an option since it would introduce huge
    // accessibility problems due to lost information

    // Make sure no value is undefined
    if (
      [
        this.textRef.clientHeight,
        this.wrapperRef.clientHeight,
        this.metaRef.clientHeight,
        this.backgroundImageRef.clientHeight,
        this.quotationRef.clientWidth,
      ].some((v) => typeof v !== 'number')
    ) {
      return;
    }

    if (!this.textOverflows) {
      // check if text needs to be moved below the image
      // The 56px is the padding of the quote container plus the space between the text and the meta
      const availableSpace = this.wrapperRef.clientHeight - this.metaRef.clientHeight - 56;

      const textOverflows = this.textRef.clientHeight > availableSpace;

      if (textOverflows) {
        this.quotationWidthAtLastDetectedOverflow = this.quotationRef.clientWidth;
        this.textOverflows = true;
      }
    } else {
      // check if text needs to be moved on the image
      const isQuoteOnFullImage = this.backgroundImageRef.clientWidth < 500; // Above 500px, the quote takes only 50% of the space

      const availableSpace50PercentWidth = this.backgroundImageRef.clientHeight;
      const availableSpaceFullWidth = (this.backgroundImageRef.clientWidth - 48) * 2.2; // The image will get 120% padding bottom (for aspect ratio) when the text is on it. 48 is padding x
      const availableSpace = isQuoteOnFullImage ? availableSpaceFullWidth : availableSpace50PercentWidth;

      const requiredSpaceQuotation50PercentWidth = this.textRef.clientHeight * 2; // Note: this is a heuristic as due to word breaks, this will never be exactly *2 in 50% of the space
      const requiredSpaceQuotationFullWidth = this.textRef.clientHeight;
      const requiredSpaceQuotation = isQuoteOnFullImage
        ? requiredSpaceQuotationFullWidth
        : requiredSpaceQuotation50PercentWidth;
      const requiredSpaceMetaAndPadding50PercentWidth = this.metaRef.clientHeight + 56;
      const requiredSpaceMetaAndPaddingFullWidth = this.metaRef.clientHeight + 24; // Only gap between meta and quote is needed since padding y is accounted for in the available space calculation
      const requiredSpaceMetaAndPadding = isQuoteOnFullImage
        ? requiredSpaceMetaAndPaddingFullWidth
        : requiredSpaceMetaAndPadding50PercentWidth;

      const textOverflows = availableSpace < requiredSpaceMetaAndPadding + requiredSpaceQuotation;

      if (textOverflows) return;

      // Avoid false negatives due to the heuristic calculation here that would lead to infinite changes.
      // Only the calculation if the text is on the image is exact.
      if (
        this.quotationRef.clientWidth > this.quotationWidthAtLastDetectedOverflow ||
        (this.quotationWidthAtLastDetectedOverflow > 500 && this.quotationRef.clientWidth < 500)
      ) {
        this.textOverflows = false;
      }
    }
  }

  private get hasImage() {
    return this.body.style === 'on-image' && !!this.body.backgroundImage.src;
  }

  private renderBackgroundImage() {
    if (!this.hasImage) return null;

    const { src, objectPosition, alt, copyright } = (this.body as UiCmsQuotationBodyOnImage).backgroundImage;

    return (
      <ui-cms-image
        ref={(ref) => {
          this.backgroundImageRef = ref;
        }}
        style={{
          '--copyright': `'© ${copyright}'`,
        }}
        class={{
          'ui-cms-quotation__background-image': !this.textOverflows,
          'sd-copyright ui-cms-quotation__background-image--overflow': this.textOverflows,
        }}
        objectPosition={objectPosition}
        src={src}
        alt={alt}
        size={2400}
      />
    );
  }

  private renderAuthorImage() {
    if (this.hasImage) return null;
    if (!(this.body as UiCmsQuotationBodySimple).authorImage) return null;

    const { src, alt, objectPosition } = (this.body as UiCmsQuotationBodySimple).authorImage;

    return (
      <ui-cms-image
        class="ui-cms-quotation__meta__author-image"
        objectPosition={objectPosition}
        src={src}
        alt={alt}
        size={240}
      />
    );
  }

  private get invertText() {
    const invertedByProp = this.inverted && !this.hasImage;
    const invertedByImage = this.hasImage && (this.body as UiCmsQuotationBodyOnImage).colorScheme === 'dark-bg';
    const preventedByOverflow = this.textOverflows;
    return (invertedByProp || invertedByImage) && !preventedByOverflow;
  }

  private renderMeta() {
    const hasAuthorImage = !this.hasImage && !!(this.body as UiCmsQuotationBodySimple).authorImage;
    const hasNameOrDescription = !!this.body.author || !!this.body.authorInfo;

    if (!hasAuthorImage && !hasNameOrDescription) return null;

    return (
      <div
        class="ui-cms-quotation__meta"
        ref={(el) => {
          this.metaRef = el;
        }}
      >
        {this.renderAuthorImage()}
        {hasNameOrDescription && (
          <div>
            {this.body.author && (
              <p
                class={{
                  'sd-paragraph ui-cms-quotation__meta__author-name': true,
                  'sd-paragraph--inverted': this.invertText,
                }}
              >
                {this.body.author}
              </p>
            )}
            {this.body.authorInfo && (
              <p
                class={{
                  'sd-paragraph sd-paragraph--size-sm': true,
                  'sd-paragraph--inverted': this.invertText,
                }}
              >
                {this.body.authorInfo}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  private get copyrightStyle() {
    if (this.body.style !== 'on-image' || !this.body.backgroundImage.copyright || this.textOverflows) return {};

    return {
      '--copyright': `'© ${this.body.backgroundImage.copyright}'`,
    };
  }

  private get overlayPositionClasses() {
    if (!this.hasImage || this.textOverflows) return {};

    const classes = {};

    ['right', 'left', 'top', 'bottom'].forEach((position) => {
      if ((this.body as UiCmsQuotationBodyOnImage).position?.includes(position)) {
        classes[`ui-cms-quotation__quote-container--position-${position}`] = true;
      }
    });

    if ((this.body as UiCmsQuotationBodyOnImage).colorScheme === 'dark-bg') {
      classes['ui-cms-quotation__quote-container--dark-bg'] = true;
    }

    return classes;
  }

  render() {
    return (
      <div
        class={{
          [`ui-cms-quotation ui-cms-quotation--${this.invertText ? 'white' : 'black'}`]: true,
          'ui-cms-quotation--with-image sd-copyright': this.hasImage && !this.textOverflows,
        }}
        style={this.copyrightStyle}
        ref={(ref) => {
          this.quotationRef = ref;
        }}
      >
        <div class="ui-cms-quotation__wrapper">
          {this.renderBackgroundImage()}
          <div
            ref={(el) => {
              this.wrapperRef = el;
            }}
            class={{
              'ui-cms-quotation__quote-container': true,
              'ui-cms-quotation__quote-container--with-image': this.hasImage && !this.textOverflows,
              'ui-cms-quotation__quote-container--bg-high-contrast':
                this.body.style === 'on-image' && this.body.highContrast,
              ...this.overlayPositionClasses,
            }}
          >
            <q
              ref={(el) => {
                this.textRef = el;
              }}
            >
              {this.body.quotation}
            </q>
            {this.renderMeta()}
          </div>
        </div>
      </div>
    );
  }
}
